import React, { useState, useRef, useEffect } from 'react';
import DatePickerLib from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaCalendarAlt } from 'react-icons/fa';
import { cn } from '../../../utils/cn';

export interface DatePickerProps {
  label?: string;
  helperText?: string;
  errorText?: string;
  value?: string; // mm/dd/yyyy format
  onChange?: (date: string) => void; // Returns mm/dd/yyyy format
  placeholder?: string;
  fullWidth?: boolean;
  required?: boolean;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
  id?: string;
}

export const DatePicker: React.FC<DatePickerProps> = ({
  label,
  helperText,
  errorText,
  value,
  onChange,
  placeholder = 'mm/dd/yyyy',
  fullWidth = false,
  required = false,
  disabled = false,
  minDate,
  maxDate,
  className,
  id,
}) => {
  const inputId = id || `datepicker-${Math.random().toString(36).substr(2, 9)}`;
  const hasError = !!errorText;
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const datePickerRef = useRef<DatePickerLib>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Convert mm/dd/yyyy string to Date object
  const parseDate = (dateString: string): Date | null => {
    if (!dateString) return null;
    if (dateString.includes('/')) {
      const [month, day, year] = dateString.split('/');
      const parsedMonth = parseInt(month);
      const parsedDay = parseInt(day);
      const parsedYear = parseInt(year);
      if (isNaN(parsedMonth) || isNaN(parsedDay) || isNaN(parsedYear)) return null;
      return new Date(parsedYear, parsedMonth - 1, parsedDay);
    }
    return null;
  };

  // Convert Date object to mm/dd/yyyy string
  const formatDate = (date: Date | null): string => {
    if (!date) return '';
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const selectedDate = parseDate(value || '');

  const handleDateChange = (date: Date | null) => {
    if (onChange) {
      onChange(formatDate(date));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  const handleCalendarClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        isOpen
      ) {
        setIsOpen(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen]);

  return (
    <div ref={containerRef} className={cn('relative', fullWidth && 'w-full')}>
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium mb-1"
          style={{ color: 'var(--color-text-primary)' }}
        >
          {label}
          {required && (
            <span className="ml-1" style={{ color: 'var(--color-error-500)' }}>
              *
            </span>
          )}
        </label>
      )}

      <div className="relative">
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          value={value || ''}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
          onFocus={(e) => {
            setIsFocused(true);
            if (!hasError) {
              e.target.style.borderColor = 'var(--color-primary-500)';
              e.target.style.boxShadow = '0 0 0 1px var(--color-primary-500)';
            }
          }}
          onBlur={(e) => {
            setIsFocused(false);
            if (!hasError) {
              e.target.style.borderColor = 'var(--color-border-light)';
              e.target.style.boxShadow = 'none';
            }
          }}
          className={cn(
            'w-full rounded-lg border transition-colors focus:outline-none focus:ring-1 px-3 py-2 pr-10 text-sm',
            hasError && 'border-red-500',
            className
          )}
          style={{
            backgroundColor: 'var(--color-background-paper)',
            borderColor: hasError
              ? 'var(--color-error-500)'
              : isFocused
              ? 'var(--color-primary-500)'
              : 'var(--color-border-light)',
            color: 'var(--color-text-primary)',
            boxShadow: isFocused && !hasError ? '0 0 0 1px var(--color-primary-500)' : 'none',
            position: 'relative',
            zIndex: 1,
          }}
        />
        <button
          type="button"
          onClick={handleCalendarClick}
          disabled={disabled}
          className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer hover:opacity-70 transition-opacity z-10"
          style={{
            color: 'var(--color-text-secondary)',
            backgroundColor: 'transparent',
            border: 'none',
          }}
          aria-label="Open calendar"
        >
          <FaCalendarAlt />
        </button>
        {isOpen && (
          <section 
            style={{ 
              position: 'absolute', 
              top: '100%', 
              left: 0, 
              zIndex: 1000, 
              marginTop: '4px',
              backgroundColor: 'var(--color-background-paper)',
              borderRadius: '0.5rem',
              border: '1px solid var(--color-border-light)',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            }}
            role="dialog"
            aria-label="Date picker calendar"
          >
            <DatePickerLib
              ref={datePickerRef}
              selected={selectedDate}
              onChange={(date: Date | null) => {
                handleDateChange(date);
                setIsOpen(false);
              }}
              dateFormat="MM/dd/yyyy"
              disabled={disabled}
              minDate={minDate}
              maxDate={maxDate}
              inline
            />
          </section>
        )}
      </div>

      {hasError && errorText && (
        <p id={`${inputId}-error`} className="mt-1 text-xs" style={{ color: 'var(--color-error-500)' }}>
          {errorText}
        </p>
      )}

      {!hasError && helperText && (
        <p id={`${inputId}-helper`} className="mt-1 text-xs opacity-70" style={{ color: 'var(--color-text-secondary)' }}>
          {helperText}
        </p>
      )}

      <style>{`
        .react-datepicker-wrapper {
          width: 100%;
        }
        .react-datepicker {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          background-color: var(--color-background-paper);
          border-color: var(--color-border-light);
          color: var(--color-text-primary);
        }
        .react-datepicker__month-container {
          background-color: var(--color-background-paper);
        }
        .react-datepicker__header {
          background-color: var(--color-background-paper);
          border-bottom-color: var(--color-border-light);
        }
        .react-datepicker__current-month,
        .react-datepicker-time__header,
        .react-datepicker-year-header {
          color: var(--color-text-primary);
        }
        .react-datepicker__day-name {
          color: var(--color-text-secondary);
        }
        .react-datepicker__day {
          color: var(--color-text-primary);
        }
        .react-datepicker__day--outside-month {
          color: var(--color-text-secondary);
          opacity: 0.5;
        }
        .react-datepicker__day:hover {
          background-color: var(--color-primary-500);
          color: white;
        }
        .react-datepicker__day--selected {
          background-color: var(--color-primary-500);
          color: white;
        }
        .react-datepicker__day--keyboard-selected {
          background-color: var(--color-primary-500);
          color: white;
        }
        .react-datepicker__day--today {
          font-weight: bold;
        }
        .react-datepicker__day--disabled {
          color: var(--color-text-secondary);
          opacity: 0.4;
        }
        .react-datepicker__navigation-icon::before {
          border-color: var(--color-text-secondary);
        }
        .react-datepicker__navigation:hover *::before {
          border-color: var(--color-text-primary);
        }
        .react-datepicker__triangle {
          display: none;
        }
      `}</style>
    </div>
  );
};

